import { Alert, Button, Checkbox, Modal, Skeleton } from 'antd'
import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { ProjectStatus, projectStatusName } from '../../common/constants'
import { JobListItem } from '../JobList/jobListItem'
import { useJobWorkareaStatuses } from './jobKanbanApi'
import { KanbanColumn, labelFor } from './jobKanbanModel'
import { defaultSelection, missesTheTarget, selectableWorkareas, statusAfterSetting } from './workareaPickerModel'
import './WorkareaPickerModal.css'

type Props = {
  job: JobListItem
  status: ProjectStatus
  columns: KanbanColumn[]
  saving: boolean
  onCancel: () => void
  onConfirm: (workareaIds: number[]) => void
}

const accentFor = (columns: KanbanColumn[], status: ProjectStatus | null) =>
  columns.find(column => column.status === status)?.accent ?? '#8b95a5'

const StatusPill = ({ status, columns, faded }: {
  status: ProjectStatus | null
  columns: KanbanColumn[]
  faded?: boolean
}) => <span
  className={`workarea-picker__pill${faded ? ' workarea-picker__pill--faded' : ''}`}
  style={{ borderColor: accentFor(columns, status), color: accentFor(columns, status) }}
>
  <span className="workarea-picker__pill-dot" style={{ backgroundColor: accentFor(columns, status) }} />
  {status === null ? 'No status' : projectStatusName(status)}
</span>

const WorkareaPickerModal = ({ job, status, columns, saving, onCancel, onConfirm }: Props) => {
  const query = useJobWorkareaStatuses(job.id)
  const workareas = query.data?.workareas ?? []
  const selectable = selectableWorkareas(workareas)
  const [selectedIds, setSelectedIds] = useState<number[]>([])

  useEffect(() => {
    if (query.data) {
      setSelectedIds(defaultSelection(query.data.workareas))
    }
  }, [query.data])

  const target = labelFor(columns, status)
  const outcome = statusAfterSetting(workareas, selectedIds, status)
  const willMiss = missesTheTarget(workareas, selectedIds, status)
  const heldBack = workareas.length - selectable.length
  const allSelected = selectedIds.length === selectable.length && selectable.length > 0

  return <Modal
    open
    width={520}
    title={`Move to ${target}`}
    okText={selectedIds.length === 1
      ? `Set 1 work area to ${target}`
      : `Set ${selectedIds.length} work areas to ${target}`}
    okButtonProps={{ disabled: selectedIds.length === 0 || query.isLoading }}
    confirmLoading={saving}
    onOk={() => onConfirm(selectedIds)}
    onCancel={onCancel}
  >
    <p className="workarea-picker__job">{job.name}</p>
    <p className="workarea-picker__why">
      This job&apos;s status is decided by its work areas, so pick the ones moving to {target}.
    </p>

    {query.isLoading && <Skeleton active paragraph={{ rows: 3 }} />}

    {query.isError && <Alert type="error" showIcon message="The work areas could not be loaded." />}

    {!query.isLoading && !query.isError && selectable.length === 0 && <Alert
      type="warning"
      showIcon
      message="This job has no work areas that count towards its status."
      description="Every work area is on hold, or the job has none, so the board cannot change its status."
    />}

    {selectable.length > 0 && <>
      <div className="workarea-picker__head">
        <span className="workarea-picker__head-label">
          Work areas · {selectedIds.length} of {selectable.length} moving
        </span>
        <Button
          type="link"
          size="small"
          className="workarea-picker__select-all"
          onClick={() => setSelectedIds(allSelected ? [] : selectable.map(x => x.id))}
        >
          {allSelected ? 'Clear all' : 'Select all'}
        </Button>
      </div>

      <div className="workarea-picker__list">
        {selectable.map(workarea => {
          const moving = selectedIds.includes(workarea.id)
          return <label key={workarea.id} className="workarea-picker__item">
            <Checkbox
              checked={moving}
              aria-label={workarea.name ?? `Work area ${workarea.id}`}
              onChange={e => setSelectedIds(ids => e.target.checked
                ? [...ids, workarea.id]
                : ids.filter(id => id !== workarea.id))}
            />
            <span className="workarea-picker__name">{workarea.name || `Work area ${workarea.id}`}</span>
            <span className="workarea-picker__change">
              <StatusPill status={workarea.status} columns={columns} faded={moving} />
              {moving && <>
                <FontAwesomeIcon icon={faArrowRight} className="workarea-picker__arrow" />
                <StatusPill status={status} columns={columns} />
              </>}
            </span>
          </label>
        })}
      </div>

      <Alert
        className="workarea-picker__outcome"
        type={willMiss ? 'warning' : 'success'}
        showIcon
        message={outcome === null
          ? 'This job status will be cleared, so the job will leave the board.'
          : willMiss
            ? `This job status will be updated to ${projectStatusName(outcome)}, not ${target}.`
            : `This job status will be updated to ${projectStatusName(outcome)}.`}
        description={willMiss
          ? `${target} needs every work area to match, so the card will land in ${
            outcome === null ? 'no column' : projectStatusName(outcome)} instead.`
          : undefined}
      />

      {heldBack > 0 && <p className="workarea-picker__held">
        {heldBack} work area{heldBack === 1 ? '' : 's'} on hold {heldBack === 1 ? 'is' : 'are'} not
        shown, because work areas on hold do not count towards the status.
      </p>}
    </>}
  </Modal>
}

export default WorkareaPickerModal
